// frontend/src/hooks/useAuth.js
import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';

const useAuth = (redirectIfUnauthorized = true) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [userData, setUserData] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const navigate = useNavigate();


  const fetchUserData = async (shouldRedirect = redirectIfUnauthorized, attempt = 1) => {
    const token = localStorage.getItem('auth-token');
    if (!token) {
      if (redirectIfUnauthorized && shouldRedirect) navigate('/account');
      setAuthLoading(false);
      setUserData(null);
      return;
    }

    // Clear the session locally — ONLY for genuine auth failures (not transient errors)
    const logout = (alertMsg) => {
      localStorage.removeItem('auth-token');
      setUserData(null);
      window.dispatchEvent(new Event('storage'));
      window.dispatchEvent(new CustomEvent('auth-change', { detail: { loggedOut: true } }));
      if (redirectIfUnauthorized && shouldRedirect) {
        if (alertMsg) alert(alertMsg);
        navigate('/account');
      }
    };

    try {
      const response = await fetch(`${backendUrl}/auth/getuserdata`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'auth-token': token,
        },
      });
      const result = await response.json();

      if (result.success) {
        setUserData({
          name: result.user.name,
          email: result.user.email,
          role: result.user.role,
          profileImage: result.user.profileImage || null,
          avatar: result.user.avatar || '',
          isVerified: result.user.isVerified, // Capture verification status
        });
        // If the user is not verified, optionally redirect to a verification prompt
        if (!result.user.isVerified && shouldRedirect) {
          navigate('/resend-verification');
        }
      } else if (result.code === 'SESSION_REPLACED') {
        logout('Your session has expired. You have been logged in from another device.');
      } else if (result.code === 'TOKEN_EXPIRED') {
        logout('Your session has expired. Please login again.');
      } else if (response.status === 401) {
        // Genuine invalid/missing token
        logout();
      } else {
        // Transient backend error (5xx, cold start, DB warmup) — do NOT log out.
        throw new Error(result.message || `HTTP ${response.status}`);
      }
    } catch (error) {
      // Network error or transient backend failure → KEEP the token (don't force re-login).
      // Retry a couple of times to ride out a Lambda cold start, then give up silently.
      if (attempt < 3) {
        setTimeout(() => fetchUserData(shouldRedirect, attempt + 1), 1500);
        return; // keep authLoading true while retrying
      }
      console.error('Could not verify session (transient error), keeping token:', error);
    }
    setAuthLoading(false);
  };

  // Trigger initial fetch on component mount
  useEffect(() => {
    fetchUserData();

    // Listen for storage changes (e.g., login from another tab)
    const handleStorageChange = () => {
      fetchUserData();
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const refreshUserData = () => {
    fetchUserData();
  };

  return { userData, authLoading, refreshUserData };
};

export default useAuth;
